// src/services/BunnyUploader.js
import { auth as firebaseAuth } from "../firebase";

export default class BunnyUploader {
  /**
   * Uploads a file to Bunny CDN via the backend /bunnyupload endpoint.
   * @param {File} file - The file to upload.
   * @param {Function} onProgress - Optional callback for upload progress (0-100).
   * @returns {Promise<string>} - The uploaded file URL.
   */
  static async upload(file, onProgress) {
    if (!file) throw new Error("No file provided");

    const idToken = await firebaseAuth.currentUser?.getIdToken();
    if (!idToken) {
      throw new Error("Authentication required for upload");
    }

    const formData = new FormData();
    formData.append("file", file);

    // Use the BACKEND domain explicitly as a fallback to avoid relative path errors (admin domain vs api domain)
    const apiBaseUrl = process.env.REACT_APP_API_URL || "https://api.spicifyapp.com";
    
    // Construct the absolute upload URL to prevent redirects or domain mismatch
    const uploadUrl = `${apiBaseUrl.replace(/\/+$/, "")}/api/bunnyupload/`;
    
    console.log(`[DEBUG] Attempting upload to: ${uploadUrl}`);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", uploadUrl);

      xhr.setRequestHeader("Authorization", `Bearer ${idToken}`);
      xhr.setRequestHeader("X-File-Size", String(file.size));

      if (onProgress && typeof onProgress === "function") {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = (event.loaded / event.total) * 100;
            onProgress(percentComplete);
          }
        };
      }

      xhr.onload = () => {
        const contentType = xhr.getResponseHeader("Content-Type");
        let data;
        let text = xhr.responseText.trim();
        
        console.log(`[DEBUG] Received response with status ${xhr.status}. Content-Type: ${contentType}`);
        
        try {
          if (contentType && contentType.includes("application/json")) {
            // Sometimes server responses contain garbage before/after the JSON payload
            const jsonStartIndex = text.indexOf('{');
            const jsonEndIndex = text.lastIndexOf('}');
            
            if (jsonStartIndex !== -1 && jsonEndIndex !== -1 && jsonEndIndex >= jsonStartIndex) {
              text = text.substring(jsonStartIndex, jsonEndIndex + 1);
            }

            data = JSON.parse(text);
          } else {
            // Explicit HTML detection to provide better error message
            if (text.toLowerCase().includes('<!doctype html>') || text.toLowerCase().includes('<html')) {
              const snippet = text.substring(0, 500).replace(/\s+/g, ' ');
              throw new Error(`Server returned HTML instead of JSON (${xhr.status}). Body snippet: ${snippet}...`);
            }

            const jsonStartIndex = text.indexOf('{');
            const jsonEndIndex = text.lastIndexOf('}');
            if (jsonStartIndex !== -1 && jsonEndIndex !== -1 && jsonEndIndex >= jsonStartIndex) {
              data = JSON.parse(text.substring(jsonStartIndex, jsonEndIndex + 1));
            } else {
              const snippet = (text || "[Empty Body]").substring(0, 500).replace(/\s+/g, ' ');
              throw new Error(`Server returned non-JSON response (${xhr.status}). Raw body: "${text}" | Snippet: ${snippet}...`);
            }
          }
          
          if (xhr.status >= 200 && xhr.status < 300) {
            if (data && data.fileUrl) {
              resolve(data.fileUrl);
            } else {
              reject(new Error("Upload success but no fileUrl returned from server"));
            }
          } else {
            reject(new Error(data.message || `Upload failed with status ${xhr.status}`));
          }
        } catch (e) {
          console.error("Upload response parsing error:", e);
          console.log("XHR Full Response:", xhr.responseText);
          reject(new Error(`Upload failed: ${e.message || "Invalid server response"}`));
        }
      };

      xhr.onerror = (err) => {
        console.error("XHR Network Error:", err);
        console.log("XHR Status during error:", xhr.status);
        reject(new Error("Network error during upload. This might be a CORS block or backend connection issue."));
      };
      xhr.send(formData);
    });
  }
}
